import React, { useState, useEffect } from 'react';
import { 
  FaUserCircle, FaEnvelope, FaSignInAlt, FaSignOutAlt, FaTachometerAlt,
  FaCheckCircle, FaCarSide, FaTruckLoading, FaHome, FaTimesCircle, FaBan, FaListUl, FaTrashAlt
} from 'react-icons/fa';

// --- Sub-Component: Booked Car Details (Includes Image Display Fix) ---
const BookedCarDetail = ({ order, onCancel, onDelete, userName }) => {
    const { carName, price, image, status } = order;
    
    const isDelivered = status === 'delivered';
    const isCancelled = status === 'cancelled';
    const canCancel = !isDelivered && !isCancelled;
    const canDelete = isDelivered || isCancelled; 
    
    let borderColor = 'border-amber-400';
    if (isDelivered) {
        borderColor = 'border-green-500';
    } else if (isCancelled) {
        borderColor = 'border-red-500';
    }

    return (
        <div className={`bg-slate-800 p-6 rounded-xl shadow-2xl border-4 ${borderColor} flex items-center space-x-6 transition-all duration-500`}>
            {/* Car Image (CRITICAL FIX: Robust display with fallback) */}
            <div className="w-32 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-slate-700">
                <img 
                    src={image} 
                    alt={carName} 
                    className="w-full h-full object-cover" 
                    // Ensures that even if the path fails (e.g., from old local storage data), 
                    // it shows a placeholder instead of a broken icon.
                    onError={(e) => { e.target.onerror = null; e.target.src="https://placehold.co/100x60/334155/E2E8F0?text=Car" }} 
                />
            </div>
            
            {/* Details */}
            <div className="flex-grow">
                <h3 className="text-xl font-bold text-white mb-1">{carName}</h3>
                <p className="text-slate-400 text-sm">Rental Price: <span className="text-amber-400 font-semibold">${price}/day</span></p>
                <p className={`text-sm font-semibold ${isDelivered ? 'text-green-500' : isCancelled ? 'text-red-500' : 'text-amber-400'}`}>
                    {isDelivered ? 'Successfully Delivered' : isCancelled ? `Order Cancelled by ${userName}` : 'Delivery in Progress'}
                </p>
            </div>

            {/* Action/Status Icon and Buttons */}
            <div className={`flex flex-col items-end space-y-2`}>
                <div className={`text-4xl ${isDelivered ? 'text-green-500' : isCancelled ? 'text-red-500' : 'text-amber-400'}`}>
                    {isDelivered ? <FaHome /> : isCancelled ? <FaTimesCircle /> : <FaCarSide />}
                </div>

                {/* CANCEL BUTTON (Visible during processing stages) */}
                {canCancel && (
                    <button 
                        onClick={() => onCancel(order.id, carName)}
                        className="flex items-center space-x-1 text-xs text-white bg-red-600 px-3 py-1 rounded-full hover:bg-red-700 transition duration-200"
                    >
                        <FaBan size={12} />
                        <span>Cancel Order</span>
                    </button>
                )}
                
                {/* DELETE BUTTON (Visible after delivered or cancelled) */}
                {canDelete && (
                    <button 
                        onClick={() => onDelete(order.id, carName)}
                        className="flex items-center space-x-1 text-xs text-white bg-slate-600 px-3 py-1 rounded-full hover:bg-slate-700 transition duration-200"
                    >
                        <FaTrashAlt size={12} />
                        <span>Delete Record</span>
                    </button>
                )}
            </div>
        </div>
    );
};


// --- Sub-Component: Delivery Status Visualization ---
const DeliveryTracker = ({ status }) => {
  const stages = [
    { name: "Order Confirmed", key: "confirmed", icon: FaCheckCircle },
    { name: "In Preparation", key: "processing", icon: FaTruckLoading },
    { name: "Out for Delivery", key: "out_for_delivery", icon: FaCarSide },
    { name: "Delivered", key: "delivered", icon: FaHome },
    { name: "Cancelled", key: "cancelled", icon: FaBan }, 
  ];

  const getStatusIndex = (key) => stages.findIndex(s => s.key === key);
  const currentStatusIndex = getStatusIndex(status);

  const isCancelled = status === 'cancelled';
  const displayStages = isCancelled ? [stages[0], stages[4]] : stages.slice(0, 4);
  const displayStatusIndex = isCancelled ? 1 : currentStatusIndex;

  const statusObject = stages.find(s => s.key === status) || stages[0];

  return (
    <div className="bg-slate-800 p-8 rounded-xl shadow-2xl mb-8 border-l-4 border-amber-400">
      <h2 className="text-2xl font-semibold mb-8 text-white">Delivery Progress</h2>
      
      <div className="flex justify-between relative px-4">
        {/* Horizontal Progress Line */}
        <div className="absolute top-7 left-8 right-8 h-1 bg-slate-700"></div>
        <div 
          className="absolute top-7 left-8 h-1 transition-all duration-1000 ease-in-out"
          style={{ 
            backgroundColor: isCancelled ? '#dc2626' : '#fbbf24', 
            width: isCancelled ? '85%' : `${Math.max(0, (currentStatusIndex / (stages.length - 1))) * 85}%` 
          }}
        ></div>

        {displayStages.map((stage, index) => {
          const isActive = index <= displayStatusIndex;
          const Icon = stage.icon;
          
          return (
            <div key={stage.key} className="relative z-10 w-1/4 flex flex-col items-center text-center">
              {/* Icon Circle */}
              <div 
                className={`w-10 h-10 rounded-full flex items-center justify-center mb-3 transition-colors duration-500 ${
                  isActive 
                    ? isCancelled ? 'bg-red-600 text-white shadow-lg' : 'bg-amber-400 text-slate-950 shadow-lg' 
                    : 'bg-slate-700 text-slate-400'
                }`}
              >
                <Icon size={16} />
              </div>
              
              {/* Text Label */}
              <p 
                className={`text-xs font-semibold mt-1 transition-colors ${
                  isActive 
                    ? isCancelled ? 'text-red-500' : 'text-amber-400' 
                    : 'text-slate-400'
                }`}
              >
                {stage.name}
              </p>
            </div>
          );
        })}
      </div>
      
      <p className="mt-8 text-lg font-medium text-white">
        Current Status: <span className={`${isCancelled ? 'text-red-500' : 'text-amber-400'} uppercase`}>{statusObject.name}</span>
      </p>
      {currentStatusIndex < 3 && !isCancelled && (
        <p className="text-sm text-slate-400 mt-2">
            Estimated Delivery: Within 48 hours of payment confirmation.
        </p>
      )}
      {isCancelled && (
        <p className="text-sm text-red-500 mt-2">
            This order has been cancelled and is no longer being processed.
        </p>
      )}
    </div>
  );
};


// --- Main Dashboard Component ---
const Dashboard = () => {
  const [user, setUser] = useState(null); // Stores full user object
  const [userName, setUserName] = useState('Loading...');
  const [isLoading, setIsLoading] = useState(true);
  
  const [orders, setOrders] = useState([]);
  
  const API_URL = 'http://localhost:4000/api/auth'; 

  // Function to determine the unique key for localStorage
  const getStorageKey = (userId) => `quickWheelsOrders_${userId}`;

  // Function to load orders based on the current user's ID
  const loadOrders = (userId) => {
    if (!userId) return; 
    
    const key = getStorageKey(userId);
    try {
        const storedOrders = localStorage.getItem(key);
        if (storedOrders) {
            setOrders(JSON.parse(storedOrders));
        } else {
            // Initialize with an EMPTY array if user is new
            setOrders([]);
            localStorage.setItem(key, JSON.stringify([]));
        }
    } catch (e) {
        console.error("Error loading mockUserOrders:", e);
        setOrders([]);
    }
  };

  const fetchUserDetails = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/current_user`, {
        credentials: 'include' 
      });
      
      const userData = await response.json();
      
      if (userData && userData.id) {
        setUser(userData); // Store full user object
        setUserName(userData.name);
        
        // CRITICAL FIX: Load orders *after* getting the user ID
        loadOrders(userData.id); 
        setIsLoading(false);
      } else {
        window.location.href = '/login'; 
      }
    } catch (error) {
      window.location.href = '/login'; 
    }
  };

  useEffect(() => {
    fetchUserDetails();
  }, []);
  
  const handleLogout = () => {
    window.location.href = `${API_URL}/logout`; 
  };
  
  // Helper to save current orders to the unique key
  const saveOrdersToStorage = (updatedOrders) => {
      if (user && user.id) {
          localStorage.setItem(getStorageKey(user.id), JSON.stringify(updatedOrders));
          setOrders(updatedOrders);
      }
  };


  // FIX: Cancel function uses saveOrdersToStorage
  const handleCancelOrder = (orderId, carName) => {
      if (!user || !user.id) return alert("User session failed. Please log in again.");

      if (window.confirm(`Are you sure you want to cancel the order for ${carName}? This action cannot be undone.`)) {
          
          const newOrders = orders.map(order => 
              order.id === orderId ? { ...order, status: 'cancelled' } : order
          );

          saveOrdersToStorage(newOrders);
          alert(`Order for ${carName} has been successfully cancelled!`);
      }
  };
  
  // FIX: Delete function uses saveOrdersToStorage
  const handleDeleteOrder = (orderId, carName) => {
    if (!user || !user.id) return alert("User session failed. Please log in again.");

    if (window.confirm(`Are you sure you want to delete the record for ${carName}? This will permanently remove it from your dashboard.`)) {
        
        const newOrders = orders.filter(order => order.id !== orderId);

        saveOrdersToStorage(newOrders);
        alert(`Record for ${carName} has been successfully deleted.`);
    }
  };


  const userInitial = userName.charAt(0).toUpperCase();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 pt-20">
        <FaTachometerAlt className="text-amber-400 text-6xl animate-spin" />
        <h1 className="text-white text-2xl ml-4">Loading Dashboard...</h1>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen relative bg-slate-950 text-white pt-24 px-4 sm:px-6 lg:px-8"
      style={{
        backgroundImage: "url('/assets/2021-BMW-M4-Competition.jpg')", 
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}
    >
      {/* Dark Overlay for Readability */}
      <div className="absolute inset-0 bg-black opacity-80"></div>
      
      {/* Content Container */}
      <div className="relative z-10 max-w-4xl mx-auto py-12">
        
        <div className="text-center mb-10">
          <div className="w-24 h-24 rounded-full bg-amber-400 text-slate-950 font-bold text-4xl flex items-center justify-center mx-auto mb-4 border-4 border-slate-700 shadow-xl">
            {userInitial}
          </div>
          <h1 className="text-4xl font-extrabold text-white">
            Welcome Back, <span className="text-amber-400">{userName}</span>!
          </h1>
          <p className="text-slate-400 text-lg mt-2">Your dedicated Quick Wheels portal.</p>
        </div>
        
        {/* --- Multi-Order Tracking Section --- */}
        <div className="bg-slate-900 p-6 rounded-xl shadow-2xl mb-8 border-l-4 border-amber-400">
            <h2 className="text-2xl font-semibold mb-6 flex items-center text-white border-b border-slate-700 pb-3">
                <FaListUl className="mr-3 text-amber-400" /> Your Orders ({orders.length})
            </h2>
            
            {orders.map((order) => (
                <div key={order.id} className="mb-8 p-4 border border-slate-700 rounded-lg">
                    {/* Booked Car Details (Image, Name, and CANCEL/DELETE BUTTONS) */}
                    <BookedCarDetail 
                        order={order} 
                        onCancel={handleCancelOrder} 
                        onDelete={handleDeleteOrder} 
                        userName={userName} 
                    />

                    {/* Delivery Status Tracker (Linked to the specific order's status) */}
                    <DeliveryTracker status={order.status} />
                </div>
            ))}

            {orders.length === 0 && (
                <p className="text-slate-400 text-center py-4">You have no active or completed orders. Rent a car now!</p>
            )}
        </div>
        {/* --- End Multi-Order Tracking Section --- */}


        {/* Profile Information Card */}
        <div className="bg-slate-800 p-8 rounded-xl shadow-2xl mb-8 border-l-4 border-amber-400 transition-all duration-300 hover:shadow-[0_0_40px_rgba(251,191,36,0.3)]">
          <h2 className="text-2xl font-semibold mb-6 flex items-center text-white">
            <FaUserCircle className="mr-3 text-amber-400" /> Account Details
          </h2>
          
          <div className="space-y-4">
            <div className="flex justify-between items-center border-b border-slate-700 pb-2">
                <span className="font-medium text-slate-400">Full Name</span>
                <span className="font-semibold text-white">{userName}</span>
            </div>
            <div className="flex justify-between items-center border-b border-slate-700 pb-2">
                <span className="font-medium text-slate-400">Email Address</span>
                <span className="font-semibold text-white flex items-center">
                    <FaEnvelope className="mr-2 text-slate-500" /> {user?.email}
                </span>
            </div>
            <div className="flex justify-between items-center">
                <span className="font-medium text-slate-400">Authentication Method</span>
                <span className="font-semibold text-green-400 flex items-center">
                    <FaSignInAlt className="mr-2 text-green-400" /> Google OAuth
                </span>
            </div>
          </div>
        </div>

        {/* Action Card */}
        <div className="bg-slate-800 p-8 rounded-xl shadow-2xl">
          <h2 className="text-2xl font-semibold mb-6 text-white">Quick Actions</h2>
          <button
            onClick={handleLogout}
            className="flex items-center space-x-2 bg-red-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-red-700 transition duration-300 shadow-md"
          >
            <FaSignOutAlt />
            <span>Sign Out of Quick Wheels</span>
          </button>
        </div>
        
      </div>
    </div>
  );
}

export default Dashboard;